/**
 * AccountDeleteSection
 *
 * Standalone "Account löschen"-Sektion. Steht in der Profilseite ganz unten,
 * damit sie nicht versehentlich vor anderen Einstellungen wahrgenommen wird.
 *
 * Backend: DELETE /auth/user/account (Selbst-Löschung). Cleanup-Cron räumt
 * Inaktive nach 14 bzw. 180 Tagen separat ab.
 */

import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import toast from '../../utils/toast';
import { TWHoldButton } from '../ui/TWHoldButton';
import { ProfileSection } from './ProfileSection';

export const AccountDeleteSection: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleDelete = async (): Promise<void> => {
    try {
      setDeletingAccount(true);
      await apiClient.delete('/auth/user/account');
      toast.success('Dein Account wurde erfolgreich gelöscht');
      logout();
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen des Accounts');
      setDeletingAccount(false);
    }
  };

  return (
    <ProfileSection
      title="Account löschen"
      description="Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten werden unwiderruflich gelöscht."
      variant="danger"
      layout="stacked"
    >
      {/* Layout analog zur NotificationToggleRow: Text-Block links (Beschreibung
          mit max-w-xl begrenzt), Hold-Button rechts vertikal mittig. */}
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="max-w-xl space-y-2 text-sm">
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Folgende Daten werden permanent entfernt:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-500 dark:text-gray-400">
              <li>Persönliche Daten und Einstellungen</li>
              <li>Watchlists und gespeicherte Assets</li>
              <li>Feedback und Bewertungen</li>
            </ul>
          </div>
        </div>
        <div className="shrink-0">
          <TWHoldButton
            onConfirm={handleDelete}
            holdDuration={3000}
            icon={<Trash2 />}
            text="3s halten"
            disabled={deletingAccount}
            size={32}
            variant="ghost"
          />
        </div>
      </div>
    </ProfileSection>
  );
};

export default AccountDeleteSection;
